import { useCallback, useEffect, useMemo, useState } from 'react'
import { deck, deckStartForRoute } from '../content'
import type { DeckEntry } from '../content'
import { navigate } from '../router/useRoute'

const STORAGE_KEY = 'lams-presenter-notes-visible'

export interface PresentationState {
  active: boolean
  index: number
  scene: DeckEntry['scene']
  section: DeckEntry['section']
  notesVisible: boolean
  enter: (fromRoute?: string) => void
  exit: () => void
  next: () => void
  prev: () => void
  goTo: (index: number) => void
  toggleNotes: () => void
}

function readNotesPreference(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

export function usePresentation(currentPath: string): PresentationState {
  const [active, setActive] = useState(false)
  const [index, setIndex] = useState(0)
  const [notesVisible, setNotesVisible] = useState(readNotesPreference)

  const entry = deck[index] ?? deck[0]!
  const scene = entry.scene
  const section = entry.section

  const enter = useCallback(
    (fromRoute?: string) => {
      const route = fromRoute ?? currentPath
      const startAt = deckStartForRoute(route)
      setIndex(startAt)
      setActive(true)
      const target = deck[startAt]
      if (target) navigate(target.scene.route)
    },
    [currentPath],
  )

  const exit = useCallback(() => setActive(false), [])

  const goTo = useCallback((next: number) => {
    const clamped = Math.max(0, Math.min(deck.length - 1, next))
    setIndex(clamped)
    const target = deck[clamped]
    if (target) navigate(target.scene.route)
  }, [])

  const next = useCallback(() => goTo(index + 1), [goTo, index])
  const prev = useCallback(() => goTo(index - 1), [goTo, index])

  const toggleNotes = useCallback(() => {
    setNotesVisible((v) => {
      const nextValue = !v
      try {
        window.localStorage.setItem(STORAGE_KEY, nextValue ? '1' : '0')
      } catch {
        /* private browsing; the preference simply does not persist */
      }
      return nextValue
    })
  }, [])

  useEffect(() => {
    if (!active) return
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return

      switch (e.key) {
        case 'ArrowRight':
        case 'PageDown':
          e.preventDefault()
          next()
          break
        case ' ':
          e.preventDefault()
          if (e.shiftKey) prev()
          else next()
          break
        case 'ArrowLeft':
        case 'PageUp':
          e.preventDefault()
          prev()
          break
        case 'Home':
          e.preventDefault()
          goTo(0)
          break
        case 'End':
          e.preventDefault()
          goTo(deck.length - 1)
          break
        case 'Escape':
          e.preventDefault()
          exit()
          break
        case 'n':
        case 'N':
          e.preventDefault()
          toggleNotes()
          break
        default:
          break
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [active, next, prev, goTo, exit, toggleNotes])

  /* Lock background scroll while the stage is up. */
  useEffect(() => {
    if (!active) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [active])

  return useMemo(
    () => ({ active, index, scene, section, notesVisible, enter, exit, next, prev, goTo, toggleNotes }),
    [active, index, scene, section, notesVisible, enter, exit, next, prev, goTo, toggleNotes],
  )
}
